import {
  assertOptionalFiniteNumber,
  validateNonNegative
} from "./index-wxhvgv2s.mjs";
import {
  WEBGPU_SINGLE_PASS_VERTEX,
  setupWebGpuSinglePass,
  cleanupWebGpuSinglePass,
  applyWebGpuSinglePass
} from "./index-tvfexb7s.mjs";
import {
  assertEffectParamsObject
} from "./index-4fvp93ps.mjs";

// src/chromatic-aberration/index.ts
import { Internals as Internals2 } from "remotion";

// src/chromatic-aberration/chromatic-aberration-runtime.ts
import { Internals } from "remotion";

// src/chromatic-aberration/chromatic-aberration-shaders.ts
var CHROMATIC_ABERRATION_VS = `#version 300 es
layout(location = 0) in vec2 aPos;
layout(location = 1) in vec2 aUv;
out vec2 vUv;
void main() {
	vUv = aUv;
	gl_Position = vec4(aPos, 0.0, 1.0);
}
`;
var CHROMATIC_ABERRATION_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform vec2 uOffset;

void main() {
	vec4 redSample = texture(uSource, vUv - uOffset);
	vec4 centerSample = texture(uSource, vUv);
	vec4 blueSample = texture(uSource, vUv + uOffset);

	fragColor = vec4(redSample.r, centerSample.g, blueSample.b, centerSample.a);
}
`;

// src/chromatic-aberration/chromatic-aberration-runtime.ts
var { createWebGL2ContextError } = Internals;
var compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create WebGL shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile failed: ${log ?? "(no log)"}`);
  }
  return shader;
};
var linkProgram = (gl, vs, fs) => {
  const program = gl.createProgram();
  if (!program) {
    throw new Error("Failed to create WebGL program");
  }
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link failed: ${log ?? "(no log)"}`);
  }
  return program;
};
var createProgram = (gl, vertexSource, fragmentSource) => {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = linkProgram(gl, vs, fs);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
};
var createRgbaTexture = (gl) => {
  const texture = gl.createTexture();
  if (!texture) {
    throw new Error("Failed to create WebGL texture");
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
};
var setupChromaticAberration = (target) => {
  const gl = target.getContext("webgl2", {
    premultipliedAlpha: true,
    alpha: true,
    preserveDrawingBuffer: true
  });
  if (!gl) {
    throw createWebGL2ContextError("chromatic aberration effect");
  }
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  const program = createProgram(gl, CHROMATIC_ABERRATION_VS, CHROMATIC_ABERRATION_FS);
  const vao = gl.createVertexArray();
  if (!vao) {
    throw new Error("Failed to create WebGL vertex array");
  }
  gl.bindVertexArray(vao);
  const data = new Float32Array([
    -1,
    -1,
    0,
    0,
    1,
    -1,
    1,
    0,
    -1,
    1,
    0,
    1,
    1,
    1,
    1,
    1
  ]);
  const vbo = gl.createBuffer();
  if (!vbo) {
    throw new Error("Failed to create WebGL buffer");
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
  gl.bindVertexArray(null);
  const textureSource = createRgbaTexture(gl);
  return {
    gl,
    program,
    vao,
    vbo,
    textureSource,
    uniforms: {
      uSource: gl.getUniformLocation(program, "uSource"),
      uOffset: gl.getUniformLocation(program, "uOffset")
    }
  };
};
var cleanupChromaticAberration = (state) => {
  const { gl, program, vao, vbo, textureSource } = state;
  gl.deleteTexture(textureSource);
  gl.deleteBuffer(vbo);
  gl.deleteProgram(program);
  gl.deleteVertexArray(vao);
};
var drawFullscreenQuad = (state) => {
  const { gl, vao } = state;
  gl.bindVertexArray(vao);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.bindVertexArray(null);
};
var applyChromaticAberration = ({
  state,
  source,
  width,
  height,
  amount,
  angle,
  flipSourceY
}) => {
  const { gl, program, textureSource, uniforms } = state;
  const radians = angle * Math.PI / 180;
  const x = Math.cos(radians) * amount / width;
  const y = Math.sin(radians) * amount / height;
  gl.viewport(0, 0, width, height);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipSourceY);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(program);
  if (uniforms.uSource)
    gl.uniform1i(uniforms.uSource, 0);
  if (uniforms.uOffset)
    gl.uniform2f(uniforms.uOffset, x, y);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  drawFullscreenQuad(state);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.useProgram(null);
};

// src/chromatic-aberration/webgpu-shader.ts
var CHROMATIC_ABERRATION_WEBGPU_SHADER = `
${WEBGPU_SINGLE_PASS_VERTEX}
@group(0) @binding(0) var source: texture_2d<f32>;
@group(0) @binding(1) var sourceSampler: sampler;
@group(0) @binding(2) var<uniform> params: Params;
struct Params { offset: vec4f, }
@fragment fn fragment(input: VertexOutput) -> @location(0) vec4f {
  let red = textureSampleLevel(source, sourceSampler, input.uv - params.offset.xy, 0);
  let center = textureSampleLevel(source, sourceSampler, input.uv, 0);
  let blue = textureSampleLevel(source, sourceSampler, input.uv + params.offset.xy, 0);
  return vec4f(red.r, center.g, blue.b, center.a);
}
`;

// src/chromatic-aberration/index.ts
var { createEffect } = Internals2;
var DEFAULT_CHROMATIC_ABERRATION_AMOUNT = 8;
var DEFAULT_CHROMATIC_ABERRATION_ANGLE = 0;
var chromaticAberrationSchema = {
  amount: {
    type: "number",
    min: 0,
    max: 100,
    step: 1,
    default: DEFAULT_CHROMATIC_ABERRATION_AMOUNT,
    description: "Amount",
    hiddenFromList: false
  },
  angle: {
    type: "rotation-degrees",
    step: 1,
    default: DEFAULT_CHROMATIC_ABERRATION_ANGLE,
    description: "Angle"
  }
};
var resolve = (params) => ({
  amount: params.amount ?? DEFAULT_CHROMATIC_ABERRATION_AMOUNT,
  angle: params.angle ?? DEFAULT_CHROMATIC_ABERRATION_ANGLE
});
var validateChromaticAberrationParams = (params) => {
  assertEffectParamsObject(params, "Chromatic Aberration");
  assertOptionalFiniteNumber(params.amount, "amount");
  assertOptionalFiniteNumber(params.angle, "angle");
  const resolved = resolve(params);
  validateNonNegative(resolved.amount, "amount");
};
var chromaticAberration = createEffect({
  type: "dev.remotion.effects.chromaticAberration",
  label: "chromaticAberration()",
  documentationLink: "https://www.remotion.dev/docs/effects/chromatic-aberration",
  calculateKey: (params) => {
    const resolved = resolve(params);
    return `chromatic-aberration-${resolved.amount}-${resolved.angle}`;
  },
  implementations: {
    webgpu: Internals2.createEffectImplementation({
      setup: (target) => setupWebGpuSinglePass(target, "chromatic-aberration"),
      apply: ({ source, width, height, params, state, gpuDevice }) => {
        if (!gpuDevice) {
          throw new Error("A WebGPU device is required for the chromatic-aberration effect");
        }
        const r = resolve(params);
        const radians = r.angle * Math.PI / 180;
        const uniformData = new Float32Array([
          Math.cos(radians) * r.amount / width,
          -(Math.sin(radians) * r.amount) / height,
          0,
          0
        ]);
        applyWebGpuSinglePass({
          state,
          source,
          width,
          height,
          device: gpuDevice,
          shader: CHROMATIC_ABERRATION_WEBGPU_SHADER,
          uniformData
        });
      },
      cleanup: cleanupWebGpuSinglePass
    }),
    webgl2: Internals2.createEffectImplementation({
      setup: (target) => setupChromaticAberration(target),
      apply: ({ source, width, height, params, state, flipSourceY }) => {
        const resolved = resolve(params);
        applyChromaticAberration({
          state,
          source,
          width,
          height,
          amount: resolved.amount,
          angle: resolved.angle,
          flipSourceY
        });
      },
      cleanup: (state) => cleanupChromaticAberration(state)
    })
  },
  schema: chromaticAberrationSchema,
  validateParams: validateChromaticAberrationParams
});
export {
  chromaticAberration
};
